import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, getAuthToken } from "../lib/apiClient";
import { Button } from "./ui/button";
import { toast } from "sonner";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  pass: "bg-emerald-50 text-emerald-800 border-emerald-300",
  warn: "bg-amber-50 text-amber-900 border-amber-300",
  fail: "bg-rose-50 text-rose-800 border-rose-300",
  manual_required: "bg-indigo-50 text-indigo-800 border-indigo-300",
};

const STATUS_LABEL = {
  pass: "PASS",
  warn: "WARN",
  fail: "FAIL",
  manual_required: "MANUAL",
};

/**
 * Panel del revisor con checklist 7 ítems + acciones aprobar / solicitar cambios / preview PDF.
 *
 * Props:
 *   caseId: id del caso a revisar.
 *   caseState: estado actual del caso (para habilitar/deshabilitar acciones).
 *   onApproved: callback tras aprobación exitosa (refrescar caso).
 *   onChangesRequested: callback tras solicitud de cambios.
 */
export default function ReviewerChecklistPanel({ caseId, caseState, onApproved, onChangesRequested }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [revisionComment, setRevisionComment] = useState("");
  const [showRevision, setShowRevision] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/reviewer/cases/${caseId}/diagnosis/checklist`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el checklist");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const setItem = (id, patch) => {
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((it) => (it.id === id ? { ...it, ...patch } : it));
      return { ...prev, items };
    });
  };

  const persistChecklist = useCallback(async (items) => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((it) => ({
          id: it.id,
          reviewer_marked: !!it.reviewer_marked,
          reviewer_reason: it.reviewer_reason || "",
        })),
      };
      const { data: d } = await api.post(`/reviewer/cases/${caseId}/diagnosis/checklist`, payload);
      setData((prev) => ({ ...prev, ...d, items: d.items, complete: d.complete, problems: d.problems }));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }, [caseId]);

  const handleToggle = (id) => {
    const items = (data?.items || []).map((it) =>
      it.id === id ? { ...it, reviewer_marked: !it.reviewer_marked } : it,
    );
    setData((prev) => ({ ...prev, items }));
    persistChecklist(items);
  };

  const handleReasonBlur = (id, reason) => {
    const items = (data?.items || []).map((it) =>
      it.id === id ? { ...it, reviewer_reason: reason } : it,
    );
    setData((prev) => ({ ...prev, items }));
    persistChecklist(items);
  };

  const handlePreview = async () => {
    setActing(true);
    try {
      const url = `${REACT_APP_BACKEND_URL}/api/reviewer/cases/${caseId}/diagnosis/preview-pdf`;
      const token = getAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(url, { credentials: "include", headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener");
      // No revocamos inmediato: dejamos que la pestaña lo use.
    } catch (e) {
      toast.error(e.message || "No se pudo previsualizar");
    } finally {
      setActing(false);
    }
  };

  const handleApprove = async () => {
    setActing(true);
    try {
      const { data: d } = await api.post(`/reviewer/cases/${caseId}/diagnosis/approve`, {
        comments: approveComment,
      });
      toast.success(`Diagnóstico aprobado. Doc ${d.document_id.slice(0, 8)} (${d.size_bytes} bytes).`);
      onApproved?.(d);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.problems) {
        toast.error(`Checklist incompleto: ${detail.problems.join(", ")}`);
      } else {
        toast.error(formatApiError(detail) || "No se pudo aprobar");
      }
    } finally {
      setActing(false);
    }
  };

  const handleRequestChanges = async () => {
    if (revisionComment.trim().length < 10) {
      toast.error("El comentario debe tener al menos 10 caracteres.");
      return;
    }
    setActing(true);
    try {
      const { data: d } = await api.post(`/reviewer/cases/${caseId}/diagnosis/request-changes`, {
        comments: revisionComment,
      });
      toast.success("Cambios solicitados al administrador para regeneración.");
      onChangesRequested?.(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo solicitar cambios");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 bg-gray-50 p-6 text-sm text-gray-500" data-testid="checklist-loading">
        Cargando checklist…
      </div>
    );
  }
  if (!data) return null;

  const reviewBlocked = caseState !== "nivel1_needs_human_review";
  const checked = (data.items || []).filter((i) => i.reviewer_marked).length;
  const total = data.items?.length || 0;

  return (
    <div className="border-2 border-primary/30 bg-white" data-testid="reviewer-checklist-panel">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-primary font-medium">
            Checklist de revisión humana
          </p>
          <h2 className="font-serif text-2xl text-gray-900 mt-1">
            7 ítems · {checked}/{total} marcados
          </h2>
          {data.human_review_status && data.human_review_status !== "pending" && (
            <p className="text-xs text-gray-500 mt-1">
              Estado de revisión:{" "}
              <span className="font-medium" data-testid="review-status-label">
                {data.human_review_status}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={acting}
            className="rounded-sm h-9 border-gray-300"
            data-testid="reviewer-preview-pdf-btn"
          >
            Vista previa PDF
          </Button>
          {saving && <span className="text-xs text-gray-400">Guardando…</span>}
        </div>
      </div>

      <ul className="divide-y divide-gray-200">
        {data.items.map((it) => (
          <li key={it.id} className="px-6 py-5" data-testid={`checklist-item-${it.id}`}>
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                checked={!!it.reviewer_marked}
                onChange={() => handleToggle(it.id)}
                disabled={reviewBlocked}
                className="mt-1 h-5 w-5 rounded-sm border-gray-300 accent-primary"
                data-testid={`checklist-toggle-${it.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs bg-gray-100 border border-gray-200 px-1.5 py-0.5 text-gray-700">
                    {it.id}
                  </span>
                  <span className="font-medium text-gray-900">{it.title}</span>
                  <span
                    className={`inline-flex border px-2 py-[2px] text-[10px] uppercase tracking-wider ${STATUS_COLOR[it.auto_status] || ""}`}
                    data-testid={`checklist-status-${it.id}`}
                  >
                    {STATUS_LABEL[it.auto_status] || it.auto_status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{it.description}</p>
                <p className="text-xs text-gray-700 italic mb-2" data-testid={`checklist-evidence-${it.id}`}>
                  <strong>Auto-check:</strong> {it.auto_evidence}
                </p>
                {(it.auto_status === "fail" || it.auto_status === "manual_required") && (
                  <textarea
                    defaultValue={it.reviewer_reason || ""}
                    onBlur={(e) => handleReasonBlur(it.id, e.target.value)}
                    rows={2}
                    placeholder="Justificación obligatoria (mínimo 10 caracteres)…"
                    disabled={reviewBlocked}
                    className="w-full mt-1 border border-gray-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid={`checklist-reason-${it.id}`}
                  />
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {reviewBlocked ? (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-xs text-gray-500" data-testid="checklist-blocked-banner">
          Este caso no está en estado `nivel1_needs_human_review`. Las acciones de aprobar / solicitar cambios están deshabilitadas.
        </div>
      ) : (
        <div className="px-6 py-5 bg-gray-50 border-t border-gray-200 space-y-4" data-testid="checklist-actions">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">
              Comentario de aprobación (opcional)
            </label>
            <textarea
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              rows={2}
              placeholder="Observaciones, notas para audit log…"
              className="w-full border border-gray-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="reviewer-approve-comment"
            />
          </div>
          <div className="flex items-center flex-wrap gap-3">
            <Button
              onClick={handleApprove}
              disabled={acting || !data.complete}
              className="rounded-sm h-11 bg-emerald-700 hover:bg-emerald-800"
              data-testid="reviewer-approve-button"
            >
              {acting ? "Procesando…" : "Aprobar y generar PDF"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowRevision(!showRevision)}
              disabled={acting}
              className="rounded-sm border-amber-400 text-amber-900 hover:bg-amber-50 h-11"
              data-testid="reviewer-request-changes-toggle"
            >
              {showRevision ? "Cancelar" : "Solicitar cambios"}
            </Button>
            {!data.complete && (
              <span className="text-xs text-rose-700" data-testid="checklist-incomplete-msg">
                Checklist incompleto: {data.problems?.join(", ")}
              </span>
            )}
          </div>
          {showRevision && (
            <div className="border-t border-gray-200 pt-4" data-testid="reviewer-revision-block">
              <label className="block text-[11px] uppercase tracking-widest text-amber-900 mb-1">
                Detalle de los cambios a realizar (≥10 caracteres)
              </label>
              <textarea
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                rows={3}
                placeholder="Describe puntualmente qué debe corregir el motor IA al regenerar…"
                className="w-full border border-amber-400 px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="reviewer-revision-comment"
              />
              <Button
                onClick={handleRequestChanges}
                disabled={acting || revisionComment.trim().length < 10}
                className="rounded-sm h-10 mt-3 bg-amber-700 hover:bg-amber-800"
                data-testid="reviewer-request-changes-submit"
              >
                Confirmar solicitud de cambios
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
